import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Clock, ChevronRight, Linkedin, Link2, Check } from "lucide-react";
import { getPostBySlug, BLOG_POSTS } from "@/lib/blogData";
import SEOHead from "@/components/SEOHead";

const CATEGORY_COLORS = {
  "Pay & Earnings": "text-primary bg-primary/10",
  "Lifestyle":      "text-blue-400 bg-blue-400/10",
  "Business":       "text-orange-400 bg-orange-400/10",
  "Getting Started":"text-green-400 bg-green-400/10",
};

function buildPostSchema(post) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "NewsArticle",
        "headline": post.title,
        "description": post.description,
        "keywords": post.keywords,
        "datePublished": post.date,
        "dateModified": post.date,
        "image": post.heroImage
          ? { "@type": "ImageObject", "url": post.heroImage, "width": 1264, "height": 848 }
          : undefined,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://hylascompanion.com/blog/${post.slug}`,
        },
        "author": {
          "@type": "Organization",
          "name": "Hylas Companion Trucking",
          "url": "https://hylascompanion.com",
        },
        "publisher": {
          "@type": "Organization",
          "name": "Hylas Companion Trucking",
          "url": "https://hylascompanion.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/4723859dfc697053410c88fca6f1c41af1f0aa1c43fb8f500fdcd4f43fe0f00e.jpeg",
            "width": 512,
            "height": 512,
          },
        },
        "url": `https://hylascompanion.com/blog/${post.slug}`,
        "isPartOf": { "@type": "WebSite", "url": "https://hylascompanion.com" },
        "about": { "@type": "Thing", "name": "Truck Driving Careers" },
        "inLanguage": "en-US",
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home",             "item": "https://hylascompanion.com" },
          { "@type": "ListItem", "position": 2, "name": "Driver Resources", "item": "https://hylascompanion.com/blog" },
          { "@type": "ListItem", "position": 3, "name": post.title,         "item": `https://hylascompanion.com/blog/${post.slug}` },
        ],
      },
    ],
  };
}

function renderBlock(block, i) {
  switch (block.type) {
    case "intro":
      return (
        <p key={i} className="text-xl text-foreground/80 leading-relaxed font-light mb-8 border-l-4 border-primary pl-5">
          {block.text}
        </p>
      );
    case "h2":
      return (
        <h2 key={i} className="font-head text-2xl font-bold uppercase tracking-[0.08em] text-[#0A0A0A] mt-12 mb-5">
          {block.text}
        </h2>
      );
    case "p":
      return (
        <p key={i} className="text-base text-foreground/75 leading-relaxed mb-5">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul key={i} className="mb-6 space-y-2">
          {block.items.map((item, j) => (
            <li key={j} className="flex items-start gap-3 text-sm text-foreground/75">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      );
    case "box":
      return (
        <div key={i} className="my-8 p-5 bg-[#0A0A0A] border-l-4 border-primary">
          <p className="font-head text-xs uppercase tracking-[0.25em] text-primary mb-2">{block.label}</p>
          <p className="text-white/80 text-sm leading-relaxed">{block.text}</p>
        </div>
      );
    case "table":
      return (
        <div key={i} className="my-8 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#0A0A0A]">
                {block.headers.map((h) => (
                  <th key={h} className="font-head text-[11px] uppercase tracking-[0.15em] text-primary px-4 py-3 text-left border border-white/5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? "bg-[#FAFAFA]" : "bg-white"}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 text-foreground/75 border border-border/40">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { postSlug } = useParams();
  const post = getPostBySlug(postSlug);
  const related = BLOG_POSTS.filter((p) => p.slug !== postSlug).slice(0, 3);
  const [copied, setCopied] = useState(false);

  const postUrl = `https://hylascompanion.com/blog/${postSlug}`;
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`;

  function handleCopyLink() {
    navigator.clipboard.writeText(postUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <SEOHead title="Article Not Found" description="This article does not exist." path={`/blog/${postSlug}`} noindex />
        <h1 className="font-head text-3xl font-bold">Article Not Found</h1>
        <Link to="/blog" className="text-primary underline">Back to Driver Resources</Link>
      </div>
    );
  }

  return (
    <div data-testid={`blog-post-${post.slug}`}>
      <SEOHead
        title={post.title}
        description={post.description}
        keywords={post.keywords}
        path={`/blog/${post.slug}`}
        schema={buildPostSchema(post)}
        ogImage={post.heroImage || undefined}
      />

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-36 pb-14 md:pt-44 md:pb-18 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-white/30 font-head uppercase tracking-[0.15em] mb-6">
            <Link to="/" className="hover:text-white/60 transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-white/60 transition-colors">Resources</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/50 truncate max-w-[180px]">{post.title}</span>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <span className={`font-head text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-sm ${CATEGORY_COLORS[post.category] || ""}`}>
              {post.category}
            </span>
            <span className="text-white/30 text-xs flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> {post.readTime} read
            </span>
          </div>

          <h1 className="font-head text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-[0.08em] text-white leading-tight">
            {post.title}
          </h1>

          {/* Share buttons */}
          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
            <span className="font-head text-[10px] uppercase tracking-[0.2em] text-white/30">Share</span>
            <a
              href={linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-linkedin"
              aria-label="Share on LinkedIn"
              className="inline-flex items-center gap-2 font-head text-[11px] uppercase tracking-[0.15em] text-white/60 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 transition-colors duration-200"
            >
              <Linkedin className="w-3.5 h-3.5" />
              LinkedIn
            </a>
            <button
              onClick={handleCopyLink}
              data-testid="share-copy-link"
              aria-label="Copy article link"
              className="inline-flex items-center gap-2 font-head text-[11px] uppercase tracking-[0.15em] text-white/60 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 transition-colors duration-200"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-primary" /> : <Link2 className="w-3.5 h-3.5" />}
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      {post.heroImage && (
        <div className="w-full bg-[#0A0A0A]">
          <div className="max-w-5xl mx-auto">
            <img
              src={post.heroImage}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover"
              loading="lazy"
            />
          </div>
        </div>
      )}

      {/* Article body */}
      <article className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {post.content.map((block, i) => renderBlock(block, i))}
        </div>
      </article>

      {/* Inline CTA */}
      <section className="bg-primary py-14 md:py-18">
        <div className="max-w-3xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div>
            <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.1em] text-black">
              See What You'd Earn at Hylas
            </h2>
            <p className="mt-1 text-black/70 text-sm">Nine pay packages. Use the calculator to see real numbers.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Link
              to="/#calculator"
              className="inline-flex items-center gap-2 font-head text-xs uppercase tracking-[0.15em] border-2 border-black text-black px-5 py-3 hover:bg-black hover:text-primary transition-colors duration-200"
            >
              Calculator
            </Link>
            <Link
              to="/apply"
              className="inline-flex items-center gap-2 font-head text-xs uppercase tracking-[0.15em] bg-black text-white px-5 py-3 hover:bg-white hover:text-black transition-colors duration-200"
            >
              Apply Now <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="py-16 bg-[#FAFAFA] border-t border-border">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <span className="font-head text-xs uppercase tracking-[0.3em] text-foreground/40">
              More Resources
            </span>
            <h2 className="font-head text-xl font-bold uppercase tracking-[0.1em] mt-3 mb-6">
              Related Articles
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="group p-5 border border-border bg-white hover:border-[#0A0A0A] transition-all duration-200"
                >
                  <span className={`font-head text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm ${CATEGORY_COLORS[p.category] || ""}`}>
                    {p.category}
                  </span>
                  <h3 className="font-head text-sm font-bold uppercase tracking-[0.05em] mt-3 leading-snug text-[#0A0A0A] group-hover:text-primary transition-colors duration-200">
                    {p.title}
                  </h3>
                  <div className="mt-3 flex items-center gap-1 text-primary/70 group-hover:text-primary transition-colors">
                    <span className="font-head text-[11px] uppercase tracking-[0.15em]">Read</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Breadcrumb nav */}
      <div className="bg-[#0A0A0A] py-4 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12 flex items-center gap-2 text-xs text-white/30 font-head uppercase tracking-[0.15em]">
          <Link to="/" className="hover:text-white/60 transition-colors">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-white/60 transition-colors">Resources</Link>
          <span>/</span>
          <span className="text-white/50 truncate">{post.title}</span>
        </div>
      </div>
    </div>
  );
}
